// Login entry-point logic for Cognito-enabled builds.
//
// Desired behavior (Cognito only):
//   - On ANY unauthenticated route, redirect to the in-app /login page so the
//     user can enter their email and be routed to the correct Identity Provider
//     (ZS Entra or client IdP) based on configured domain rules.
//   - If there's already a valid Cognito session, do nothing — the app renders
//     normally.

import { signInWithRedirect } from "aws-amplify/auth";
import { isCognitoLoginAllowed } from "src/config/loginMethods";
import { getCognitoToken, clearCognitoToken } from "./cognitoAuth";
import { environment } from "src/config/environment";

// Set by the logout handler so the /login page can show a "signed out" message.
// Consumed (read + removed) on /login mount. Uses localStorage (not
// sessionStorage) because the Cognito Hosted-UI /logout round-trip is
// cross-origin and sessionStorage does not survive it.
const SIGNED_OUT_KEY = "cognito_signed_out";

// Stashes the provider the user picked when we have to bounce through a
// Hosted-UI sign-out first (stale session). Read back on return to /login to
// auto-resume that exact provider's login. sessionStorage survives the
// cross-origin round-trip to Cognito and back within the same tab.
const PENDING_PROVIDER_KEY = "cognito_pending_login_provider";

export type AutoLoginOutcome =
	| "authenticated" // valid session — render the app
	| "show-picker"; // no session — render the /login page

const buildLoginPath = (): string => {
	const basePath = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
	return `${basePath}/login`;
};

const isOnLoginRoute = (): boolean => typeof window !== "undefined" && window.location.pathname.endsWith("/login");

/**
 * Resolve what should happen for a Cognito build on an unauthenticated entry.
 * If a valid Cognito session exists the app renders normally. Otherwise the
 * user is directed to the in-app /login page where they enter their email and
 * are routed to the correct Identity Provider.
 */
export const resolveCognitoAutoLogin = async (): Promise<AutoLoginOutcome> => {
	if (!isCognitoLoginAllowed) return "authenticated";

	const token = await getCognitoToken();
	if (token) return "authenticated";

	return "show-picker";
};

/** No-op kept for backward compatibility with callers (e.g. startInteractiveCognitoLogin). */
export const resetCognitoAutoLoginGuard = (): void => {
	// Auto-redirect was removed; nothing to guard.
};

/** No-op kept for backward compatibility with logout callers. */
export const suppressCognitoAutoLogin = (): void => {
	// Auto-redirect was removed; nothing to suppress.
};

/** Mark that the user explicitly logged out so /login shows the signed-out message. */
export const markCognitoSignedOut = (): void => {
	try {
		sessionStorage.setItem(SIGNED_OUT_KEY, "true");
	} catch {
		// non-fatal
	}
};

/** Consume the signed-out flag (read + remove). Returns true once after logout. */
export const consumeSignedOutFlag = (): boolean => {
	try {
		if (sessionStorage.getItem(SIGNED_OUT_KEY) === "true") {
			sessionStorage.removeItem(SIGNED_OUT_KEY);
			return true;
		}
	} catch {
		// non-fatal
	}
	return false;
};

/** Navigate to the in-app /login picker (honors REACT_APP_BASE_PATH). */
export const goToLoginPicker = (): void => {
	if (typeof window === "undefined" || isOnLoginRoute()) return;
	window.location.href = buildLoginPath();
};

/** Navigate to the app root (post-login landing), honoring REACT_APP_BASE_PATH. */
const enterApp = (): void => {
	const base = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
	window.location.href = `${base}/`;
};

const stashPendingProvider = (provider: string): void => {
	try {
		sessionStorage.setItem(PENDING_PROVIDER_KEY, provider);
	} catch {
		// sessionStorage unavailable (private mode) — resume just won't fire.
	}
};

/**
 * Start an interactive Cognito login for `provider` from the in-app picker.
 *
 * In Amplify v6, signInWithRedirect throws UserAlreadyAuthenticatedException
 * (and does NOT navigate) when Amplify still holds a user in local storage —
 * stale/expired tokens or a surviving Hosted-UI session. Swallowing that error
 * is what makes the login buttons appear dead. We recover by intent:
 *   - valid session  -> the user is already logged in; enter the app.
 *   - stale session  -> tear it down via a clean Hosted-UI sign-out, stashing
 *                       the chosen provider so resumePendingCognitoLogin() fires
 *                       it automatically when Cognito returns us to /login.
 */
export const startInteractiveCognitoLogin = async (provider: string): Promise<void> => {
	// Manual pick — clear the one-shot auto-login guard so this explicit attempt
	// isn't suppressed by the auto-redirect loop guard.
	resetCognitoAutoLoginGuard();
	try {
		await signInWithRedirect({ provider: { custom: provider } });
	} catch (error) {
		if ((error as { name?: string })?.name === "UserAlreadyAuthenticatedException") {
			const token = await getCognitoToken();
			if (token) {
				// Session is actually valid — they're logged in; just enter the app.
				enterApp();
				return;
			}
			// Truly stale: sign out (Hosted-UI logout round-trip back to /login)
			// and resume the chosen provider on return.
			stashPendingProvider(provider);
			await clearCognitoToken();
			return;
		}
		console.error("Cognito signInWithRedirect failed:", { provider, error });
	}
};

/**
 * On /login entry, resume a login that was interrupted by a stale-session
 * sign-out (see startInteractiveCognitoLogin). Returns true when a redirect to
 * the Hosted UI was initiated (caller should stop rendering the picker).
 */
export const resumePendingCognitoLogin = async (): Promise<boolean> => {
	let provider: string | null = null;
	try {
		provider = sessionStorage.getItem(PENDING_PROVIDER_KEY);
	} catch {
		provider = null;
	}
	if (!provider) return false;

	try {
		sessionStorage.removeItem(PENDING_PROVIDER_KEY);
	} catch {
		// non-fatal
	}

	resetCognitoAutoLoginGuard();
	try {
		await signInWithRedirect({ provider: { custom: provider } });
		return true;
	} catch (error) {
		// Local state should be clean after sign-out; if this still fails, drop
		// back to the picker rather than loop.
		console.error("Cognito resume login failed:", { provider, error });
		return false;
	}
};
